import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/20/solid';
import { ui } from '@procuros/datachecks';

import { useEmptyStructure } from 'hooks/useObjectStructure';
import { DisplayModes } from 'services/webedi/types';
import { usePackingListStore } from 'stores/packingList/packingList';
import { isPlaceholderTransportUnit } from 'support/helpers/packingList/defaultTransportUnit';
import { WebEDIRowData } from 'support/types/webediTable';
import { DataTable, useGenerateWebEDIColumns } from 'components/DataTable';
import { DataTableProvider, useDataTableContext } from 'components/DataTable/contexts/DataTableContext';
import { Button } from 'components/Form/Button';

import { useDocumentTransportUnitsConfig } from './hooks/useDocumentTransportUnitsConfig';
import { useGenerateUnitIdentifiers } from './hooks/useGenerateUnitIdentifiers';
import { usePackingListModal } from './hooks/usePackingListModal';
import { usePackingListRowFilter } from './hooks/usePackingListRowFilter';
import { DocumentSectionAnnotation } from '../DocumentSectionAnnotation/DocumentSectionAnnotation';
import { usePinnedColumns } from '../helpers';
import { PackingListModal } from '../PackingListModal/PackingListModal';
import { WebEDISection } from '../WebEDISection/WebEDISection';
import { TransportUnitsHeaderActions } from './components/TransportUnitsHeaderActions';

export const DocumentTransportUnits = memo(
  ({ config, mode, isSubmitting, baseData, revisedData }: DocumentTransportUnitsProps) => {
    const packingListEnabled = usePackingListStore((state) => state.enabled);
    const { label: title, description, options, fields } = config;
    if (!fields?.length) {
      return null;
    }
    return (
      <WebEDISection
        header={{
          title,
          spacing: 'medium',
          description,
        }}
      >
        <div className="space-y-4">
          <DocumentSectionAnnotation path={DATA_PREFIX} sectionConfig={config} />
          <DataTableProvider name={DATA_PREFIX} baseData={baseData} revisedData={revisedData}>
            <TransportUnitsTable
              numberOfPinnedColumns={options?.pinned_column_count}
              config={fields}
              mode={mode}
              isSubmitting={isSubmitting}
              packingListEnabled={packingListEnabled}
            />
          </DataTableProvider>
        </div>
      </WebEDISection>
    );
  },
);

DocumentTransportUnits.displayName = 'DocumentTransportUnits';

const TransportUnitsTable = ({
  mode,
  config,
  numberOfPinnedColumns,
  isSubmitting,
  packingListEnabled = false,
}: {
  mode: DisplayModes;
  config: ui.TransportUnitsSection['fields'];
  numberOfPinnedColumns: number | undefined;
  isSubmitting: boolean;
  packingListEnabled?: boolean;
}) => {
  const { t } = useTranslation();
  const { fields, append, baseData, revisedData } = useDataTableContext();
  const { isOpen, open, close, packingList, onApply } = usePackingListModal();
  const existingUnitIdentifiers = usePackingListStore((state) => state.existingUnitIdentifiers);
  const generateUnitIdentifiers = useGenerateUnitIdentifiers();
  const showActions = !packingListEnabled && (mode === DisplayModes.create || mode === DisplayModes.edit);
  const showBuildPackingList = packingListEnabled && (mode === DisplayModes.create || mode === DisplayModes.edit);
  const showBulkLabelActions = packingListEnabled && mode === DisplayModes.view;
  const tableConfig = packingListEnabled
    ? { ...TABLE_STATIC_CONFIG[mode], selectableRows: false, deletableRows: false }
    : TABLE_STATIC_CONFIG[mode];
  const columnFields = useDocumentTransportUnitsConfig(config, packingListEnabled);
  const columns = useGenerateWebEDIColumns({
    config: columnFields,
    ...tableConfig,
    nestedLevelField: 'unit_identifier',
    displayMode: mode,
  });
  const pinnedColumns = usePinnedColumns({ config: columnFields, numberOfPinnedColumns, ...tableConfig });
  const emptyValue = useEmptyStructure(fields);
  const { hideTransportUnitRow } = usePackingListRowFilter(packingListEnabled);

  // Mirror the row filter: the button reflects the units the user actually sees, so the initial
  // default-transport-unit state (a single hidden placeholder) reads "build", not "edit".
  const visibleTransportUnitsCount = packingListEnabled
    ? (fields as Array<WebEDIRowData>).filter((row) => !isPlaceholderTransportUnit(row)).length
    : fields.length;

  return (
    <>
      <DataTable
        pinnedColumns={pinnedColumns}
        id="transport_units"
        key={mode}
        meta={{ dataPrefix: DATA_PREFIX, mode, isSubmitting }}
        columns={columns}
        data={fields}
        hideRow={hideTransportUnitRow}
        baseData={baseData as typeof fields}
        revisedData={revisedData as typeof fields}
        enableGlobalSearch={false}
        searchPositionStrategy={packingListEnabled ? 'absolute' : 'relative'}
        emptyState={
          showActions
            ? t('webedi:transport_units.empty_state.create.text')
            : t('webedi:transport_units.empty_state.view.text')
        }
        headerMenu={
          showBuildPackingList || showBulkLabelActions ? (
            <TransportUnitsHeaderActions
              showBuildPackingList={showBuildPackingList}
              showBulkLabelActions={showBulkLabelActions}
              isSubmitting={isSubmitting}
              visibleTransportUnitsCount={visibleTransportUnitsCount}
              onBuildPackingList={open}
            />
          ) : null
        }
        footerMenu={
          showActions ? (
            <Button
              variant="secondary"
              size="extra-small"
              LeftIcon={PlusIcon}
              onClick={() => {
                const table = document.getElementById('transport_units');
                append({ ...emptyValue, _internal: {} });
                setTimeout(() => {
                  table?.scrollTo({ top: table.scrollHeight });
                }, 250);
              }}
              analyticsId="webedi:transport_units_add"
              disabled={isSubmitting}
            >
              {t('webedi:transport_units.add_button.text')}
            </Button>
          ) : null
        }
      />
      {isOpen && packingList ? (
        <PackingListModal
          lineItems={packingList.lineItems}
          transportUnits={packingList.transportUnits}
          transportUnitsConfig={config}
          existingUnitIdentifiers={existingUnitIdentifiers}
          generateUnitIdentifiers={generateUnitIdentifiers}
          onApply={onApply}
          onClose={close}
        />
      ) : null}
    </>
  );
};

const DATA_PREFIX = 'transport_units';
const TABLE_STATIC_CONFIG = {
  view: {
    withIndexColumn: true,
  },
  create: {
    withIndexColumn: true,
    selectableRows: true,
    deletableRows: true,
  },
  edit: {
    withIndexColumn: true,
    selectableRows: true,
    deletableRows: true,
  },
};

type DocumentTransportUnitsProps = {
  config: ui.TransportUnitsSection;
  mode: DisplayModes;
  isSubmitting: boolean;
  baseData?: Array<unknown>;
  revisedData?: Array<unknown>;
};
